/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Action} from "../../models/action";
import { ActionInstance } from "../../models/action-instance";
import {Rule} from "../../models/rule";
import { ActionService } from "../../services/action.service";
import {Component, Input, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";

declare var $: any;

const ID = "step4_chooseReactions";
const CAROUSEL_PAGE_LENGTH = 4;

@Component({
  selector: 'app-rule-editor-step-choose-reactions',
  templateUrl: './rule-editor-step-choose-reactions.component.html'
})
export class RuleEditorStepChooseReactionsComponent implements OnInit {
  
  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Main DIV ID, referenced here so it can be used in the .html as well as the JQuery stuff internal to this component
  mId = ID;
  
  @Input()    //Rule being edited
  mRule: Rule;

  //All known Actions/Reactions available for the Rule
  mActions: Action[];


  //Variables used by the Carousel logic
  mActionCarouselPages: ActionCarouselPage[] = [];
  mActionsPageNumber = 1;
  mActionsSearchValue = "";


  constructor(
    private mRoute: ActivatedRoute,
    private mActionService: ActionService
  ) {}



  ngOnInit(): void {
    this.mRoute.data.subscribe((data: {actions: Action[]}) => {
      this.mActions = data.actions == null ? [] : data.actions;
      this.refreshActionsCarouselPages();
    });
  }
  
  
  setVisible(visible: boolean): void {
    visible? $("#" + this.mId).show() : $("#" + this.mId).hide();
  }
  

  setActionsPageNumber(pageNumber: number): void {
    this.mActionsPageNumber = pageNumber;
  }

  refreshActionsCarouselPages(): void {
    const pages: ActionCarouselPage[] = [];
    if (this.mActions != null) {
      this.mActions.forEach(action => {
        //Figure out if the Action should be added to the Carousel depending on the Search Value
        const add = this.mActionsSearchValue == null || this.mActionsSearchValue.length == 0 ? true : action.name.toLowerCase().includes(this.mActionsSearchValue.toLowerCase());

        if (add) {
          //Create a new Page if no Page currently exists or the last Page is already at the max number of size
          if (pages.length == 0 || pages[pages.length - 1].mActions.length >= CAROUSEL_PAGE_LENGTH) {
            const newPage = new ActionCarouselPage();
            newPage.mId = pages.length + 1;
            pages.push(newPage);
          }

          //Add the Action to the last Page
          pages[pages.length - 1].mActions.push(action);
        }
      });
    }
    this.mActionCarouselPages = pages;
    this.mActionsPageNumber = 1;
  }

  getGlyphForAction(action: Action): string {
    return action.glyph == null || action.glyph.length == 0 ? "fa-question" : action.glyph;
  }

  actionSelected(action: Action): void {
    console.log("actionSelected() - Action: " + (action == null ? "*NULL*" : (action.id + "/" + action.name)));
    this._actionSelected(action);
  }
  
  private async _actionSelected(action: Action): Promise<void> {
    //Construct a new ActionInstance for the selected Action to be added to the Rule's list of Actions
    const newAction = new ActionInstance();
    
    //Retrieve the Action from the Service using the ID to pull in the properties/all other information
    try {
      newAction.action = await this.mActionService.getActionById(action.id);
    } catch (error) {
      this.handleError(error);
      return;
    }
    
    //Add the new ActionInstance to the Rule's list of Actions
    if (this.mRule.actions == null) {
      this.mRule.actions = [ newAction ];
    } else {
      this.mRule.actions.push(newAction);
    }
  }
  
  /*
   * Removes the given ActionInstance from the Rule being edited
   */
  removeAction(action: ActionInstance): void {
    if (this.mRule.actions != null) {
      const index = this.mRule.actions.indexOf(action);
      if (index > -1) {
        this.mRule.actions.splice(index, 1);
      }
    }
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}

class ActionCarouselPage {
  mId: number;
  mActions: Action[] = [];
}
